import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Plus, X } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { EditableText } from "@/components/EditableText";
import { Reveal } from "@/components/Reveal";

const OFFSETS = [70, -50, 90, -70];
const SPANS = ["md:col-span-5", "md:col-span-4 md:mt-24", "md:col-span-4", "md:col-span-5 md:-mt-12"];
const RATIOS = ["aspect-[4/5]", "aspect-square", "aspect-[3/4]", "aspect-[4/3]"];

const GalleryItem = ({ item, index, progress }) => {
  const { admin, removeItem } = useSite();
  const y = useTransform(progress, [0, 1], [OFFSETS[index % 4], -OFFSETS[index % 4]]);
  return (
    <motion.figure style={{ y }} className={SPANS[index % 4]} data-testid={`gallery-item-${index}`}>
      <div className={`overflow-hidden border border-[#EADDCF] ${RATIOS[index % 4]}`}>
        <img
          src={item.url}
          alt={item.caption}
          className="h-full w-full object-cover"
          data-testid={`gallery-image-${index}`}
        />
      </div>
      <figcaption className="mt-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#5C564D]">
        <EditableText path={`gallery.${index}.caption`} testId={`gallery-caption-${index}`} />
        {admin && (
          <button
            data-testid={`gallery-remove-${index}`}
            onClick={() => removeItem("gallery", index)}
            className="text-[#DA121A]"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </figcaption>
      {admin && (
        <EditableText
          path={`gallery.${index}.url`}
          testId={`gallery-url-${index}`}
          className="mt-1 block w-full break-all text-[10px] normal-case tracking-normal text-[#5C564D]/60"
        />
      )}
    </motion.figure>
  );
};

const Gallery = () => {
  const ref = useRef(null);
  const { content, admin, addItem } = useSite();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  return (
    <section id="gallery" ref={ref} data-testid="gallery-section" className="scroll-mt-16 bg-white px-6 py-28 md:px-12 md:py-40">
      <div className="mx-auto max-w-[1400px]">
        <Reveal>
          <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.3em] text-[#5C564D]">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#DA121A]" aria-hidden="true" />
            <EditableText path="gallery_heading" testId="gallery-heading" />
          </div>
        </Reveal>
        <div className="mt-16 grid gap-10 md:grid-cols-12">
          {content.gallery.map((item, i) => (
            <GalleryItem key={i} item={item} index={i} progress={scrollYProgress} />
          ))}
        </div>
        {admin && (
          <button
            data-testid="gallery-add"
            onClick={() => addItem("gallery", { url: "https://", caption: "New photograph" })}
            className="mt-16 flex items-center gap-1 text-[11px] uppercase tracking-widest text-[#078930]"
          >
            <Plus className="h-3 w-3" /> Add photograph
          </button>
        )}
      </div>
    </section>
  );
};

export default Gallery;
